import { useState, useCallback, forwardRef, useRef, useEffect } from 'react';
import AsyncSelect from 'react-select/async';
import debounce from 'lodash.debounce';
import { SearchIcon, LocationPinIcon } from 'nexticons/outline';
import { ErrorMessageUI } from '@next-order/next-order-components';
import classNames from 'classnames';
import {
  trimAddress,
  convertPostcodeDataToGmapsFormat,
} from 'helpers/addressHelper';
import { useSelector } from 'react-redux';

const CustomOption = (props) => {
  const { data, innerRef, innerProps, selectProps, isFocused } = props;
  const allOptions = selectProps.options;
  const isLast = allOptions[allOptions.length - 1]?.value === data.value;
  const isSavedAddress = data.isSavedAddress;
  const isSeparator = data.isSeparator;
  const labelName =
    data?.labelName && data?.labelName?.toLowerCase() !== 'address'
      ? `${data.labelName} - `
      : '';

  if (isSeparator) {
    return (
      <div
        ref={innerRef}
        {...innerProps}
        className='px-3 py-2 text-xs font-medium text-gray-500 bg-gray-100 border-b border-gray-200'
      >
        {data.label}
      </div>
    );
  }

  return (
    <div
      ref={innerRef}
      {...innerProps}
      className={classNames(
        'grid-cols-50px-1 grid min-h-12.25 w-full cursor-pointer items-center gap-x-1 py-2 text-left text-gray-500 transition-colors duration-300 hover:bg-gray-50 hover:text-primary-title focus:bg-gray-50 focus:text-primary-title',
        {
          'border-b border-gray-200': !isLast,
          'bg-blue-50': isSavedAddress,
          'bg-gray-100': isFocused,
        }
      )}
    >
      <span className='flex h-full w-full items-center justify-center'>
        <LocationPinIcon
          className={classNames('text-gray-500', {
            'text-blue-500': isSavedAddress,
            'text-gray-600': isFocused,
          })}
          width={26}
          strokeWidth={1.5}
        />
      </span>
      <span
        className={classNames(
          'flex items-center justify-between text-3.75 font-normal',
          {
            'font-medium': isSavedAddress,
            'text-gray-700': isFocused,
          }
        )}
      >
        <div>
          {labelName && (
            <span className='text-3.75 font-semibold'>{labelName}</span>
          )}

          <span className='text-3.75 font-normal'>{data.label}</span>
        </div>
        {isSavedAddress && (
          <span className='mr-2 text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded'>
            Saved
          </span>
        )}
      </span>
    </div>
  );
};

const CustomLoader = () => (
  <div className='flex items-center justify-center py-4'>
    <div className='flex space-x-0.5'>
      <div className='w-2 h-2 bg-gray-500 rounded-full animate-pulse'></div>
      <div
        className='w-2 h-2 bg-gray-500 rounded-full animate-pulse'
        style={{ animationDelay: '0.2s' }}
      ></div>
      <div
        className='w-2 h-2 bg-gray-500 rounded-full animate-pulse'
        style={{ animationDelay: '0.4s' }}
      ></div>
    </div>
  </div>
);

const DEFAULT_SEARCH_TYPES = ['street_address', 'premise', 'subpremise'];

const parseAddressComponents = (addressComponents) => {
  if (!addressComponents || !Array.isArray(addressComponents)) {
    return { mainText: '', secondaryText: '' };
  }

  let streetNumber = '';
  let route = '';
  let locality = '';
  let adminArea = '';

  addressComponents.forEach((component) => {
    const types = component.types || [];

    if (types.includes('street_number')) {
      streetNumber = component.long_name;
    } else if (types.includes('route')) {
      route = component.long_name;
    } else if (types.includes('locality')) {
      locality = component.long_name;
    } else if (types.includes('administrative_area_level_1')) {
      adminArea = component.short_name;
    }
  });

  const mainText = [streetNumber, route].filter(Boolean).join(' ');
  const secondaryText = [locality, adminArea].filter(Boolean).join(' ');

  return { mainText, secondaryText };
};

const AddressAutocomplete = forwardRef(
  (
    {
      className = '',
      errorMessage = '',
      extendedClassName = '',
      address = '',
      radius = '2000',
      deliveryAddrError,
      currency,
      menuItemsLength,
      deliveryCost,
      placeholder = 'Enter delivery address',
      types = DEFAULT_SEARCH_TYPES,
      onSelect,
      updateOrderDeliveryAddress = () => {},
      onClearError = () => {},
      storeConfig,
      shouldSelectSavedAddress = false,
    },
    ref
  ) => {
    const [cachedOptions, setCachedOptions] = useState([]);
    const cacheRef = useRef({});
    const [selectedValue, setSelectedValue] = useState(null);
    const [defaultOptions, setDefaultOptions] = useState([]);
    const [isLoadingAddress, setIsLoadingAddress] = useState(false);

    const savedAddresses = useSelector(
      (state) => state.homeReducer?.savedAddresses || []
    );

    useEffect(() => {
      if (address) {
        setSelectedValue({ label: address });
      }
    }, [address]);

    useEffect(() => {
      if (
        !address &&
        savedAddresses?.addresses?.length > 0 &&
        shouldSelectSavedAddress
      ) {
        const addresses = savedAddresses.addresses;
        let selectedAddress = addresses.find((addr) => addr.isDefault);

        if (!selectedAddress) {
          selectedAddress = addresses[0];
        }
        if (selectedAddress) {
          handleSelect({
            description: selectedAddress.addressLine1,
            label: selectedAddress.addressLine1,
            placeId: selectedAddress.placeId,
            isSavedAddress: true,
            savedAddressData: selectedAddress,
          });
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shouldSelectSavedAddress, savedAddresses?.addresses]);

    const getSavedAddressOptions = useCallback(() => {
      return savedAddresses?.addresses
        ?.map((savedAddr) => {
          return {
            labelName: savedAddr.label,
            label: `${savedAddr.addressLine1}`,
            description: `${savedAddr.addressLine1}, ${savedAddr.addressLine2}`,
            placeId: savedAddr.placeId,
            value: savedAddr.placeId,
            isSavedAddress: true,
            savedAddressData: savedAddr,
            isDefault: savedAddr.isDefault,
            hasCustomLabel:
              savedAddr?.label && savedAddr?.label?.toLowerCase() !== 'address',
          };
        })
        ?.sort((a, b) => {
          if (a.isDefault && !b.isDefault) return -1;
          if (!a.isDefault && b.isDefault) return 1;

          if (a.hasCustomLabel && !b.hasCustomLabel) return -1;
          if (!a.hasCustomLabel && b.hasCustomLabel) return 1;

          return 0;
        });
    }, [savedAddresses?.addresses]);

    const updateDefaultOptions = useCallback(() => {
      const savedOptions = getSavedAddressOptions();
      const allCachedOptions = Object.values(cacheRef.current).flat();

      const combinedOptions = [...savedOptions];

      if (savedOptions.length > 0 && allCachedOptions.length > 0) {
        combinedOptions.push({
          label: 'Recent Searches',
          value: 'separator',
          isSeparator: true,
          isDisabled: true,
        });
      }

      allCachedOptions.forEach((cachedOption) => {
        const isDuplicate = combinedOptions.some(
          (option) => option.placeId === cachedOption.placeId
        );
        if (!isDuplicate) {
          combinedOptions.push(cachedOption);
        }
      });

      setDefaultOptions(combinedOptions);
    }, [getSavedAddressOptions]);

    useEffect(() => {
      updateDefaultOptions();
    }, [updateDefaultOptions]);

    useEffect(() => {
      updateDefaultOptions();
    }, [cachedOptions, updateDefaultOptions]);

    const fetchSuggestions = async (input) => {
      if (!input || input.length === 0) {
        const savedOptions = getSavedAddressOptions();
        const allCachedOptions = Object.values(cacheRef.current).flat();

        const combinedOptions = [...savedOptions];

        if (savedOptions.length > 0 && allCachedOptions.length > 0) {
          combinedOptions.push({
            label: 'Recent Searches',
            value: 'separator',
            isSeparator: true,
            isDisabled: true,
          });
        }

        allCachedOptions.forEach((cachedOption) => {
          const isDuplicate = combinedOptions.some(
            (option) => option.placeId === cachedOption.placeId
          );
          if (!isDuplicate) {
            combinedOptions.push(cachedOption);
          }
        });

        return combinedOptions;
      }

      if (input.length < 3) {
        const substringMatches = Object.entries(cacheRef.current)
          .filter(([key]) => key.includes(input))
          .flatMap(([, options]) => options);

        return substringMatches;
      }

      if (cacheRef.current[input]) {
        return cacheRef.current[input];
      }

      const { lat, long } = storeConfig?.addressLocation || {};

      const searchTypes =
        storeConfig.countryIdentifier === 'GB'
          ? [...types, 'postal_code']
          : types;

      const res = await fetch(
        `${storeConfig.cloudFunctionsUrl}/addressSearch`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            input,
            types: searchTypes,
            country: storeConfig?.countryIdentifier,
            requestFrom: 'website',
            restaurantId: storeConfig?.restaurantId,
            storeId: storeConfig?.storeId,
            location: {
              lat,
              lng: long,
            },
            radius: Number(radius),
          }),
        }
      );

      const data = await res.json();
      const predictions = data?.results?.predictions || [];
      const postcodeData = data?.postcodeData;

      const mappedOptions = [];

      if (
        postcodeData &&
        postcodeData.addresses &&
        postcodeData.addresses.length > 0
      ) {
        const postcodeOptions = postcodeData.addresses.map((address, index) => {
          const formattedLines = address.formatted_address.filter((line) =>
            line.trim()
          );
          const mainAddress = formattedLines[0] || address.line_1 || '';
          const secondaryAddress =
            formattedLines.slice(1).filter(Boolean).join(', ') ||
            [address.town_or_city, address.county].filter(Boolean).join(', ');

          const label =
            trimAddress({
              mainAddress,
              secondryAddress: secondaryAddress,
              storeConfig,
            })?.trim() || mainAddress;

          const uniqueIdentifier =
            `${postcodeData.postcode}_${address.line_1}_${index}`.replace(
              /\s+/g,
              ''
            );
          const placeId = `postcode_${uniqueIdentifier}`;

          return {
            label,
            description: formattedLines.join(', ') || address.line_1,
            placeId: placeId,
            value: placeId,
            isPostcodeData: true,
            postcodeData: {
              postcode: postcodeData.postcode,
              latitude: postcodeData.latitude,
              longitude: postcodeData.longitude,
              address: address,
            },
          };
        });

        mappedOptions.push(...postcodeOptions);
      }

      const googleOptions = predictions.map((item) => {
        const isCustomAddress =
          item?.structured_formatting?.main_text &&
          item?.structured_formatting?.secondary_text;
        const label = trimAddress({
          mainAddress: isCustomAddress
            ? item?.structured_formatting?.main_text
            : item?.description,
          secondryAddress: item?.structured_formatting?.secondary_text,
          storeConfig,
        })?.trim();

        return {
          label,
          description: item.description,
          placeId: item.place_id,
          value: item.place_id,
          structured_formatting: item.structured_formatting,
        };
      });

      mappedOptions.push(...googleOptions);

      cacheRef.current[input] = mappedOptions;
      setCachedOptions(mappedOptions);
      return mappedOptions;
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedFetch = useCallback(
      debounce((input, callback) => {
        fetchSuggestions(input).then(callback);
      }, 300),
      []
    );

    const handleSelect = async (selected) => {
      setSelectedValue(selected);
      onClearError();
      updateOrderDeliveryAddress('', '', '', '');
      if (!selected?.placeId) {
        return;
      }

      let postcodeData = null;
      if (
        selected?.isSavedAddress &&
        selected?.savedAddressData?.additionalInformation
      ) {
        postcodeData = selected.savedAddressData.additionalInformation;
      } else if (selected?.isPostcodeData && selected?.postcodeData) {
        postcodeData = selected.postcodeData;
      }

      if (postcodeData) {
        const gmaps = convertPostcodeDataToGmapsFormat(postcodeData);

        if (!gmaps) {
          console.error('Failed to convert postcodeData to gmaps format');
          return;
        }

        const fullSuggest = {
          description: selected.description,
          label: selected.label,
          placeId: selected.placeId,
          location: {
            lat: postcodeData.latitude,
            lng: postcodeData.longitude,
          },
          gmaps: gmaps,
          isPostcodeData: true,
          postcodeData: postcodeData,
        };
        onSelect(fullSuggest);
        return;
      }

      setIsLoadingAddress(true);
      try {
        const res = await fetch(
          `${storeConfig.cloudFunctionsUrl}/getAddressByPlaceId`,
          {
            method: 'POST',
            headers: {
              'Access-Control-Allow-Origin': '*',
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              placeId: selected.placeId,
              requestFrom: 'website',
              restaurantId: storeConfig?.restaurantId,
              storeId: storeConfig?.storeId,
            }),
          }
        );

        const data = await res.json();

        const addressComponents = data?.results?.result;
        const location = data?.results?.result?.geometry?.location;

        let formattedLabel = selected.label;

        if (selected?.isSavedAddress && addressComponents?.address_components) {
          const { mainText, secondaryText } = parseAddressComponents(
            addressComponents.address_components
          );

          if (mainText) {
            formattedLabel = trimAddress({
              mainAddress: mainText,
              secondryAddress: secondaryText,
              storeConfig,
            })?.trim();
          }
        }

        const fullSuggest = {
          description: selected.description,
          label: formattedLabel,
          placeId: selected.placeId,
          location,
          gmaps: addressComponents,
        };

        onSelect(fullSuggest);
      } catch (error) {
        console.error('Error fetching address details:', error);
      } finally {
        setIsLoadingAddress(false);
      }
    };

    return (
      <>
        <div
          className={classNames(
            'relative z-10 w-full shadow-none border first:rounded-t-2.5 last:rounded-b-2.5',
            {
              'border-b-neutral-150 border-t-transparent border-x-transparent last:border-b-transparent':
                !errorMessage,
              'border-danger': !!errorMessage,
            },
            className
          )}
        >
          <div className='absolute top-6.5 left-6.5 -translate-x-1/2 -translate-y-1/2 z-20 h-8 w-8 flex items-center justify-center text-neutral-575'>
            <SearchIcon width={22} strokeWidth={2} />
          </div>

          <AsyncSelect
            ref={ref}
            isClearable
            isLoading={isLoadingAddress}
            value={selectedValue || null}
            cachedOptions={false}
            defaultOptions={defaultOptions}
            loadOptions={debouncedFetch}
            noOptionsMessage={() => null}
            onChange={handleSelect}
            getOptionLabel={(option) => option.label}
            getOptionValue={(option) => option.placeId}
            components={{
              DropdownIndicator: () => null,
              IndicatorSeparator: () => null,
              ClearIndicator: () => null,
              Option: CustomOption,
              LoadingMessage: CustomLoader,
            }}
            placeholder={placeholder}
            classNamePrefix='react-select'
            className={classNames('geosuggest-ui', extendedClassName)}
            styles={{
              control: (base) => ({
                ...base,
                padding: '4px 8px 4px 48px',
                fontSize: '16px',
                minHeight: '50px',
                border: '0px',
              }),
              menu: (base) => ({
                ...base,
                zIndex: 9999,
              }),
            }}
          />

          {address &&
          menuItemsLength > 0 &&
          deliveryCost &&
          deliveryAddrError !== true ? (
            <div
              className='deliveryCost absolute top-6.5 right-0 -translate-x-1/2 -translate-y-1/2 z-20 h-5 flex items-center justify-center text-white text-sm'
              style={{ height: 20 }}
            >
              {deliveryCost < 1
                ? 'Free'
                : `${currency}${Number(deliveryCost).toFixed(2)}`}
            </div>
          ) : null}
        </div>

        {errorMessage && (
          <ErrorMessageUI
            hideArrow
            extendedClassName='!-mt-0 rounded-none first:rounded-t-2.5 last:rounded-b-2.5'
            message={errorMessage}
          />
        )}
      </>
    );
  }
);

export default AddressAutocomplete;
